import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Plus, Search } from 'lucide-react'
import { useUpsertQuote } from '@/api/queries'
import { ApiError } from '@/api/errors'
import { Button } from '@/components/ui/Button'
import { DataTable } from '@/components/ui/DataTable'
import { Input, Select } from '@/components/ui/Field'
import { EmptyState } from '@/components/ui/States'
import { useToast } from '@/components/ui/Toast'
import { BulkActionBar } from '@/components/review/BulkActionBar'
import { ReviewSurface } from '@/components/review/ReviewSurface'
import { StatusTabs } from '@/components/review/StatusTabs'
import { useReviewCollection } from '@/components/review/useReviewCollection'
import { QuoteDrawer } from './QuoteDrawer'
import { buildColumns } from './columns'
import { speakerLine } from './speaker'
import type { Quote } from '@/types'

const searchText = (q: Quote) => `${q.text} ${q.speakerName ?? ''} ${speakerLine(q)}`

export function QuotesPage() {
  const { clientId } = useParams()
  const upsert = useUpsertQuote(clientId!)
  const toast = useToast()

  const [editing, setEditing] = useState<Quote | 'new' | null>(null)

  const collection = useReviewCollection<Quote>(clientId!, 'quotes', {
    searchText,
    noun: 'quote',
  })

  const columns = buildColumns({
    status: collection.status,
    onFavorite: collection.setFavoriteFor,
  })

  const review = useCallback(
    (id: string, status: 'approved' | 'rejected') => {
      collection.setStatusFor(id, status)
      setEditing(null)
    },
    [collection],
  )

  return (
    <>
      <ReviewSurface
        title="Quotes"
        description="Extracted attribution is often wrong. Fix the wording and the speaker before approving — only approved quotes are retrievable by the agent."
        headerActions={
          <Button variant="primary" onClick={() => setEditing('new')}>
            <Plus size={14} aria-hidden />
            Add quote
          </Button>
        }
        filters={
          <>
            <div className="relative">
              <Search
                size={13}
                aria-hidden
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint"
              />
              <Input
                value={collection.query}
                onChange={(e) => collection.setQuery(e.target.value)}
                placeholder="Search text or speaker"
                aria-label="Search quotes"
                className="h-8 w-64 pl-7 text-[13px]"
              />
            </div>
            <Select
              value={collection.tagFilter}
              onChange={(e) => collection.setTagFilter(e.target.value)}
              aria-label="Filter by tag"
              className="h-8 w-40 text-[13px]"
            >
              <option value="all">All tags</option>
              {collection.tagPool.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </>
        }
        tabs={
          <StatusTabs
            value={collection.status}
            counts={collection.counts}
            onChange={collection.setStatus}
          />
        }
        bulkBar={
          <BulkActionBar
            count={collection.selected.size}
            noun="quote"
            tagPool={collection.tagPool}
            onClear={collection.clearSelection}
            onApprove={collection.bulk.approve}
            onReject={collection.bulk.reject}
            onAddTag={collection.bulk.addTag}
            onDelete={collection.bulk.requestDelete}
          />
        }
      >
        <DataTable
          rows={collection.rows}
          columns={columns}
          rowKey={(q) => q.id}
          loading={collection.isLoading}
          selected={collection.selected}
          onSelectedChange={collection.setSelected}
          onRowClick={(q) => setEditing(q)}
          rowClassName={(q) => (q.previouslyRejected ? 'bg-warn-50/40' : undefined)}
          empty={
            <EmptyState
              title={
                collection.query || collection.tagFilter !== 'all'
                  ? 'No quotes match these filters'
                  : `No ${collection.status === 'favorites' ? 'favorited' : collection.status} quotes`
              }
              description={
                collection.all.length === 0
                  ? 'The last crawl did not produce any quotes. Add one manually, or widen the crawl scope.'
                  : undefined
              }
              action={
                <Button size="sm" onClick={() => setEditing('new')}>
                  Add quote
                </Button>
              }
            />
          }
        />
      </ReviewSurface>

      <QuoteDrawer
        clientId={clientId!}
        quote={editing}
        tagPool={collection.tagPool}
        onClose={() => setEditing(null)}
        onSave={async (patch) => {
          try {
            await upsert.mutateAsync(patch)
          } catch (e) {
            // The drawer stays open with the draft, so nothing typed is lost.
            const duplicate = e instanceof ApiError && e.status === 409
            toast.push(duplicate ? 'This quote is already in the list.' : 'Could not save the quote.', 'error')
            return
          }
          setEditing(null)
          toast.push(patch.id ? 'Quote saved.' : 'Quote added.')
        }}
        onReview={review}
        onFavorite={collection.setFavoriteFor}
      />

      {collection.deleteDialog}
    </>
  )
}
